import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ussd } from '@repletoryreach/sdk-api';

/**
 * USSD, from the platform's side (4 October 2026). Every write invalidates
 * the whole `['admin','ussd']` subtree: approving an application gives out a
 * code and changes the queue, the codes list and the settings' readiness at
 * once, and a page that shows the application waiting beside its live code
 * would be wrong in two places.
 */
const ROOT = ['admin', 'ussd'] as const;

export function useUssdSettings() {
  return useQuery({ queryKey: [...ROOT, 'settings'], queryFn: () => ussd.adminSettings(), retry: false });
}

export function useUssdApplications() {
  return useQuery({ queryKey: [...ROOT, 'applications'], queryFn: () => ussd.adminApplications(), refetchInterval: 30_000 });
}

export function useUssdCodes() {
  return useQuery({ queryKey: [...ROOT, 'codes'], queryFn: () => ussd.adminCodes(), refetchInterval: 30_000 });
}

export function useUssdSessionsSummary() {
  return useQuery({ queryKey: [...ROOT, 'sessions'], queryFn: () => ussd.adminSessionsSummary(), refetchInterval: 30_000 });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ROOT });
}

export function useUpdateUssdSettings() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (req: ussd.UssdAdminSettingsUpdate) => ussd.updateAdminSettings(req), onSettled: done });
}

export function useApproveUssdApplication() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (v: { id: string; req: ussd.UssdApproveRequest }) => ussd.approve(v.id, v.req), onSettled: done });
}

export function useRejectUssdApplication() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (v: { id: string; reason: string }) => ussd.reject(v.id, v.reason), onSettled: done });
}

export function useSuspendUssdCode() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (v: { id: string; reason?: string }) => ussd.suspend(v.id, v.reason), onSettled: done });
}

export function useResumeUssdCode() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (id: string) => ussd.resume(id), onSettled: done });
}

export function useReleaseUssdCode() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (id: string) => ussd.release(id), onSettled: done });
}
